import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  ConnectionLineType,
  Edge,
  Connection,
  useReactFlow,
  XYPosition,
  NodeChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { AxiosResponse } from "axios";
import dayjs, { Dayjs } from "dayjs";
import { FormikProps, useFormik } from "formik";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import { IMeasurementTypeOptions } from "../../../../@types/Measurements";
import { Notification } from "../../../../@types/Notification";
import {
  IWorkflow,
  IWorkflowArticleOption,
  IWorkflowResponse,
  IWorkflowRule,
  IWorkflowSettings,
  IWorkflowsSelectOption,
  IWorkflowsSurveyOption,
  IWorkflowStep,
  IWorkflowVersion,
} from "../../../../@types/Worflows";
import { Button } from "../../../../components/Button";
import { SelectDropdown } from "../../../../components/SelectDropdown";
import { NOTIFICATION } from "../../../../constants/localStorageKeys";
import {
  useHospitalGroupWards,
  useWards,
} from "../../../../hooks/queries/wards";
import {
  useHasPatientsWithCarerEmail,
  useWorkflowsSelectOptions,
} from "../../../../hooks/queries/workflows";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { useWardLabel } from "../../../../hooks/useWardLabel";
import {
  BuilderLayout,
  ISectionButton,
} from "../../../../layout/components/BuilderLayout/BuilderLayout";
import { measurementsService } from "../../../../services/measurementsService";
import { workflowsService } from "../../../../services/worflowsService";
import {
  extractErrors,
  isErrorResponse,
} from "../../../../utils/apiErrorUtils";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { safeStorage } from "../../../../utils/storage";
import { WorkflowPublishErrorsModal } from "../WorkflowPublishErrorsModal";
import { NodeLibrary } from "./components/NodesLibrary";
import { ResponseNode } from "./components/ResponseComponent";
import { RuleNode } from "./components/RuleComponent";
import { isScheduledRuleType } from "./components/RuleComponent/scheduledRuleTypes";
import { TextFieldRow } from "./components/TextFieldRow";
import { WorkflowStatusChangeModal } from "./components/WorkflowStatusChangeModal";
import {
  ErrorMessage,
  ReactFlowContainer,
  Row,
  SettingsChildContainer,
  SettingsContainer,
  SettingsLabel,
  wardsSelectCss,
} from "./WorkflowsBuilder.styles";

export function WorkflowsBuilder() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const wardLabel = useWardLabel();
  const { screenToFlowPosition, fitView } = useReactFlow();
  const { openModal, closeModal } = useModal();
  const { workflowId } = useParams();
  const [searchParams] = useSearchParams();
  const { hospitalGroupWards, isWardsFetching, isWardsRefetching } =
    useHospitalGroupWards(
      parseInt(searchParams.get("hospitalGroupId") ?? "", 10)
    );
  const {
    ruleConditionComparators,
    surveyScoreConditionComparators,
    ruleConditionTriggers,
    responseTypes,
    responseTimes,
    actionTypes,
    timezones,
    hospitalTimezone,
    dateEventTypes,
    spirometryStatuses,
    occurrencePeriods,
    isFetching,
    isRefetching,
  } = useWorkflowsSelectOptions();
  const { showToast } = useToast();

  const [workflow, setWorkflow] = useState<IWorkflow>();
  const [selectedSection, setSelectedSection] = useState<"flow" | "settings">(
    workflowId ? "flow" : "settings"
  );
  const [measurementTypeOptions, setMeasurementTypeOptions] = useState<
    IMeasurementTypeOptions[]
  >([]);
  const [surveyOptions, setSurveyOptions] = useState<IWorkflowsSurveyOption[]>(
    []
  );
  const [articleOptions, setArticleOptions] = useState<
    IWorkflowArticleOption[]
  >([]);
  const [ruleTypes, setRuleTypes] = useState<IWorkflowsSelectOption[]>([]);
  const [fibricheckStatuses, setFibricheckStatuses] = useState<
    IWorkflowsSelectOption[]
  >([]);
  const [aliveCorEcgStatuses, setAliveCorEcgStatuses] = useState<
    IWorkflowsSelectOption[]
  >([]);
  const [sleepStages, setSleepStages] = useState<IWorkflowsSelectOption[]>([]);
  const [activityMetrics, setActivityMetrics] = useState<
    IWorkflowsSelectOption[]
  >([]);
  const [firstStepId, setFirstStepId] = useState(uuidv4());
  const [nodes, setNodesState, onNodesChange] = useNodesState<any>([]);
  const [edges, setEdgesState, onEdgesChange] = useEdgesState<any>([]);
  const [recentWardsRequested, setRecentWardsRequested] = useState<number[]>(
    []
  );
  const [whiteButtonIsLoading, setWhiteButtonIsLoading] = useState(false);
  const [purpleButtonIsLoading, setPurpleButtonIsLoading] = useState(false);
  const [areOptionsLoading, setAreOptionsLoading] = useState(false);
  const [isWorkflowLoading, setIsWorkflowLoading] = useState(false);
  const [hasSettingsChanged, setHasSettingsChanged] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isLoading =
    isFetching ||
    isRefetching ||
    areOptionsLoading ||
    isWorkflowLoading ||
    isWardsFetching ||
    isWardsRefetching;

  const formValidationSchema = yup.object().shape({
    title: yup.string().max(50).required(t("workflows_builder_titleRequired")),
    description: yup
      .string()
      .required(t("workflows_builder_descriptionRequired")),
    wards: yup
      .array()
      .min(
        1,
        t("workflows_validation_wardRequired", { ward: wardLabel.singular })
      ),
  });

  const formik = useFormik<IWorkflowSettings>({
    initialValues: {
      title: "",
      description: "",
      wards: [] as number[],
      published: false,
    },
    onSubmit: () => {},
    validationSchema: formValidationSchema,
  });

  // A workflow can reference wards that fall outside the ward set returned for
  // the current user/URL context (e.g. opening an existing workflow without a
  // `hospitalGroupId`). Those selected IDs have no matching dropdown option, so
  // antd renders them as raw ward-ID numbers. Detect them once the group wards
  // have settled, then pull the full ward list only when needed to resolve
  // their names.
  const hasUnresolvedWards = useMemo(() => {
    if (isWardsFetching || !formik.values.wards?.length) return false;

    const knownWardIds = new Set(
      (hospitalGroupWards ?? []).map((ward) => ward.wardId)
    );

    return formik.values.wards.some((wardId) => !knownWardIds.has(wardId));
  }, [formik.values.wards, hospitalGroupWards, isWardsFetching]);

  const { wards: allWards } = useWards({ enabled: hasUnresolvedWards });

  const hasPatientsWithCarerEmail = useHasPatientsWithCarerEmail(
    formik.values.wards
  );

  const shouldShowErrorMessage = (field: "title" | "description" | "wards") => {
    return formik?.touched[field] ? formik?.errors[field] ?? "" : "";
  };

  // Handlers

  const handleCancel = () => {
    mixpanelActions.track("User Action: Cancel workflow");
    navigate("/workflows");
  };

  const setLoadingState = (isLoading: boolean) => {
    setWhiteButtonIsLoading(isLoading);
    setPurpleButtonIsLoading(isLoading);
  };

  const handleSaveSettings = async (isPublished?: boolean) => {
    if (!workflow) return undefined;

    const workflowSettings: IWorkflowSettings = {
      title: formik.values.title,
      description: formik.values.description,
      wards: formik.values.wards,
      published: isPublished ?? workflow.published,
    };

    const result = await workflowsService.updateWorkflow(
      workflow.id,
      workflowSettings
    );

    // A failed publish returns 400 with a list of incompatibilities. Let the
    // caller (the publish flow) surface that list instead of a generic toast.
    if (isErrorResponse(result) && extractErrors(result).length > 0) {
      setHasSettingsChanged(false);
      return result;
    }

    const notification: Notification = {
      show: true,
      message: "",
      type: "success",
      width: "max-content",
    };

    if (result.status >= 200 && result.status < 300) {
      notification.message = t("workflows_builder_settingsSaved");

      setWorkflow((prevWorkflow) => {
        if (prevWorkflow) {
          return {
            ...prevWorkflow,
            title: formik.values.title,
            description: formik.values.description,
            wards: formik.values.wards,
            published: workflow.published,
          };
        }

        return prevWorkflow;
      });
    } else {
      notification.message = t("workflows_builder_settingsFailed");
      notification.type = "error";
    }

    setHasSettingsChanged(false);
    showToast(notification.message, notification.type);

    return result;
  };

  const handleOnSaveWorkflow = async (isPublished: boolean) => {
    const createStepsFromNodes = () => {
      return nodes?.reduce((acc, node) => {
        const formikValues = node.data.ref?.current.values;

        const step: IWorkflowStep = {
          id: uuidv4(),
          positionX: node.position.x,
          positionY: node.position.y,
          stepType: formikValues.ruleType ? "Rule" : "Response",
          ruleType: formikValues.ruleType,
          conditions: formikValues.conditions,
          responseType: formikValues.responseType,
          timeType: formikValues.responseTime,
          time: (formikValues.timeUtc as Dayjs)?.utc().format("HH:mm"),
          messageText: formikValues.messageText,
          questionText: formikValues.questionText,
          option1Text: formikValues.option1Text,
          option2Text: formikValues.option2Text,
          actionType: formikValues.actionType,
          surveyId: formikValues.surveyId,
          articleId: formikValues.articleId,
          pushNotificationMessage: formikValues.pushNotificationMessage,
          measurementType: formikValues.ruleType
            ? formikValues.conditions[0].measurementType
            : formikValues.measurementType,
        };

        acc[node.id] = step;
        return acc;
      }, {} as { [id: string]: IWorkflowStep });
    };

    const assignEdgesToSteps = (steps: { [id: string]: IWorkflowStep }) => {
      const { ...tempSteps } = steps;

      edges.forEach((edge) => {
        const { source, target, sourceHandle } = edge;

        if (sourceHandle === "true") {
          tempSteps[source].nextStepOne = tempSteps[target];
        } else if (sourceHandle === "false") {
          tempSteps[source].nextStepTwo = tempSteps[target];
        }
      });

      return tempSteps[firstStepId];
    };

    setLoadingState(true);

    const workflowId = workflow?.id ?? uuidv4();
    const steps = createStepsFromNodes();
    const firstStep = assignEdgesToSteps(steps);

    const workflowVersion: IWorkflowVersion = {
      id: uuidv4(),
      firstStep,
      workflowId,
    };

    const workflowToAdd: IWorkflow = {
      id: workflowId,
      title: formik.values.title,
      description: formik.values.description,
      versions: [workflowVersion],
      wards: formik.values.wards,
      published: isPublished,
    };

    const notification: Notification = {
      show: true,
      message: "",
      type: "success",
      width: "max-content",
    };

    // When publishing, the API validates the workflow against its wards and
    // returns 400 with a list of incompatibilities. Show that list and keep
    // the user in the builder so they can fix the workflow before retrying.
    const showPublishErrorsIfAny = (result?: AxiosResponse): boolean => {
      if (!isPublished || !result || !isErrorResponse(result)) {
        return false;
      }

      const errors = extractErrors(result);

      if (errors.length === 0) {
        return false;
      }

      setLoadingState(false);
      openModal(
        <WorkflowPublishErrorsModal errors={errors} onClose={closeModal} />,
        {
          width: "479px",
          height: "100%",
          showCloseButton: true,
        }
      );

      return true;
    };

    if (workflow) {
      const addVersionResult = await workflowsService.addWorkflowVersion(
        workflow.id,
        workflowVersion
      );

      if (showPublishErrorsIfAny(addVersionResult)) {
        return;
      }

      if (addVersionResult.status >= 200 && addVersionResult.status < 300) {
        if (hasSettingsChanged || isPublished !== workflow?.published) {
          const settingsResult = await handleSaveSettings(isPublished);

          if (showPublishErrorsIfAny(settingsResult)) {
            return;
          }
        }
        notification.message = t(
          isPublished
            ? "workflows_toasts_changesPublished"
            : "workflows_toasts_changesSaved"
        );
      } else {
        notification.message = t(
          isPublished
            ? "workflows_toasts_changesPublishFailed"
            : "workflows_toasts_changesSaveFailed"
        );
        notification.type = "error";
      }
    } else {
      const addWorkflowResult = await workflowsService.addWorkflow(
        workflowToAdd
      );

      if (showPublishErrorsIfAny(addWorkflowResult)) {
        return;
      }

      if (addWorkflowResult.status >= 200 && addWorkflowResult.status < 300) {
        notification.message = t(
          isPublished ? "workflows_toasts_published" : "workflows_toasts_saved"
        );
      } else {
        notification.message = t(
          isPublished
            ? "workflows_toasts_publishFailed"
            : "workflows_toasts_saveFailed"
        );
        notification.type = "error";
      }
    }

    safeStorage.setItem(NOTIFICATION, JSON.stringify(notification));

    setLoadingState(false);
    navigate("/workflows");
  };

  const onConnect = useCallback(
    (conn: Connection) => {
      const hasSourceConnection = edges.some(
        (edge) =>
          edge.source === conn.source && edge.sourceHandle === conn.sourceHandle
      );
      const hasTargetConnection = edges.some(
        (edge) =>
          edge.target === conn.target && edge.targetHandle === conn.targetHandle
      );

      if (!hasSourceConnection && !hasTargetConnection) {
        setEdgesState((eds) => addEdge({ ...conn, type: "step" }, eds));
      }
    },
    [edges]
  );

  const handleNodesChanges = (changes: NodeChange[]) => {
    const filteredChanges = changes.filter(
      (node) => !(node.type === "remove" && node.id === firstStepId)
    );

    onNodesChange(filteredChanges);
  };

  const handleSurveyAndWardOptions = async (
    wards: number[],
    nodesToUpdate?: any[]
  ) => {
    if (
      recentWardsRequested === wards ||
      wards.length === 0 ||
      (wards === workflow?.wards &&
        (surveyOptions.length > 0 ||
          measurementTypeOptions.length > 0 ||
          articleOptions.length > 0))
    ) {
      return;
    }

    setRecentWardsRequested(wards);
    setAreOptionsLoading(true);

    const surveys = (await workflowsService.getWorflowsSurveyOptions(wards))
      .data;
    const measurementTypes = (
      await measurementsService.getMeasurementTypesByWards(wards, true)
    ).data;
    const articles = (await workflowsService.getWorflowsArticleOptions(wards))
      .data;
    const wardRuleTypes = (await workflowsService.getWorflowsRuleTypes(wards))
      .data;

    const wardFibricheckStatuses = wardRuleTypes.some(
      (rt) => rt.type === "FibricheckStatus"
    )
      ? (await workflowsService.getWorflowsFibricheckStatuses()).data
      : [];

    const wardAliveCorEcgStatuses = wardRuleTypes.some(
      (rt) => rt.type === "AliveCorEcgStatus"
    )
      ? (await workflowsService.getWorflowsAliveCorEcgStatuses()).data
      : [];

    const wardSleepStages = wardRuleTypes.some((rt) => rt.type === "Sleep")
      ? (await workflowsService.getWorflowsSleepStages()).data
      : [];

    const wardActivityMetrics = wardRuleTypes.some(
      (rt) => rt.type === "Activity"
    )
      ? (await workflowsService.getWorflowsActivityMetrics()).data
      : [];

    setMeasurementTypeOptions(measurementTypes);
    setSurveyOptions(surveys);
    setArticleOptions(articles);
    setRuleTypes(wardRuleTypes);
    setFibricheckStatuses(wardFibricheckStatuses);
    setAliveCorEcgStatuses(wardAliveCorEcgStatuses);
    setSleepStages(wardSleepStages);
    setActivityMetrics(wardActivityMetrics);

    if (nodes.length === 0 && !nodesToUpdate) {
      setAreOptionsLoading(false);
      return;
    }

    const tempNodes = nodesToUpdate ?? nodes;

    const updatedNodes = tempNodes?.map((node) => {
      if (node.type === "responseNode" && surveys.length) {
        const surveyIndex = surveys?.findIndex(
          (survey) =>
            survey.surveyId === node.data.ref?.current?.values.surveyId
        );

        if (surveyIndex === -1) {
          node.data.ref?.current?.setFieldValue("surveyId", undefined);
        }
      }

      const measurementTypeIndex = measurementTypes?.findIndex(
        (measurementType) =>
          measurementType.measurementType ===
          node.data.ref?.current?.values.measurementType
      );

      if (measurementTypeIndex === -1) {
        node.data.ref?.current?.setFieldValue("measurementType", undefined);
      }

      const articleIndex = articleOptions?.findIndex(
        (article) => article.id === node.data.ref?.current?.values.articleId
      );

      if (articleIndex === -1) {
        node.data.ref?.current?.setFieldValue("articleId", undefined);
      }

      return {
        ...node,
        data: {
          ...node.data,
          measurementTypeOptions: measurementTypes,
          surveyOptions: surveys,
          articleOptions: articles,
          ruleTypes: wardRuleTypes,
          fibricheckStatuses: wardFibricheckStatuses,
          aliveCorEcgStatuses: wardAliveCorEcgStatuses,
          sleepStages: wardSleepStages,
          activityMetrics: wardActivityMetrics,
        },
      };
    });

    setNodesState(updatedNodes);
    setAreOptionsLoading(false);
  };

  // Handle Nodes

  // Check if any rule in the workflow is a scheduled rule (Inactivity / Date): only one is allowed
  const hasScheduledRule = useMemo(() => {
    return nodes.some((node: any) => {
      if (node.type === "ruleNode" && node.data?.ref?.current) {
        return isScheduledRuleType(node.data.ref.current.values?.ruleType);
      }
      return isScheduledRuleType(node.data?.content?.ruleType);
    });
  }, [nodes]);

  const createNode = (
    step: IWorkflowStep,
    formikRef:
      | React.RefObject<FormikProps<IWorkflowRule>>
      | React.RefObject<FormikProps<IWorkflowResponse>>,
    content: IWorkflowRule | IWorkflowResponse,
    type: string
  ) => {
    const newNode: any = {
      id: step.id,
      type,
      position: { x: step.positionX || 0, y: step.positionY || 0 },
      data: {
        ruleTypes,
        ruleConditionComparators,
        surveyScoreConditionComparators,
        ruleConditionTriggers,
        responseTypes,
        responseTimes,
        actionTypes,
        surveyOptions,
        measurementTypeOptions,
        articleOptions,
        timezoneOptions: timezones,
        hospitalTimezone,
        fibricheckStatuses,
        aliveCorEcgStatuses,
        sleepStages,
        activityMetrics,
        dateEventTypes,
        spirometryStatuses,
        occurrencePeriods,
        hasPatientsWithCarerEmail,
        ref: formikRef,
        isFirstStep: step.id === firstStepId,
        hasScheduledRule,
        content,
      },
    };

    setNodesState((prevNodes: IWorkflowStep[]) => [...prevNodes, newNode]);
  };

  const addEdgeToState = (
    sourceId: string,
    targetId: string,
    sourceHandle: string
  ) => {
    const edge: Edge = {
      id: `edge-${sourceId}-${targetId}`,
      source: sourceId,
      target: targetId,
      sourceHandle,
    };
    setEdgesState((eds) => addEdge({ ...edge, type: "step" }, eds));
  };

  const addStepNodeAndEdge = (step: IWorkflowStep) => {
    if (!step || !step.id) return;

    if (step.stepType === "Rule") {
      const formikRef = React.createRef<FormikProps<IWorkflowRule>>();
      const ruleNode: IWorkflowRule = {
        id: step.id,
        ruleType: step.ruleType,
        measurementType: step.measurementType,
        conditions: step.conditions ?? [],
      };

      createNode(step, formikRef, ruleNode, "ruleNode");
    } else if (step.stepType === "Response") {
      const formikRef = React.createRef<FormikProps<IWorkflowResponse>>();
      const today = dayjs().format("YYYY-MM-DD");

      const responseNode: IWorkflowResponse = {
        id: step.id,
        measurementType: step.measurementType,
        responseType: step.responseType,
        actionType: step.actionType,
        surveyId: step.surveyId,
        articleId: step.articleId,
        responseTime: step.timeType,
        timeUtc: step.time
          ? dayjs.utc(`${today} ${step.time}`).local()
          : undefined,
        messageText: step.messageText,
        questionText: step.questionText,
        option1Text: step.option1Text,
        option2Text: step.option2Text,
      };

      createNode(step, formikRef, responseNode, "responseNode");
    }

    if (step.nextStepOne && step.nextStepOneId) {
      addEdgeToState(step.id, step.nextStepOneId, "true");
      addStepNodeAndEdge(step.nextStepOne);
    }

    if (step.nextStepTwo && step.nextStepTwoId) {
      addEdgeToState(step.id, step.nextStepTwoId, "false");
      addStepNodeAndEdge(step.nextStepTwo);
    }
  };

  const handleWorkflowNodes = async (workflow: IWorkflow) => {
    if (!workflow) return;

    const latestVersion = workflow.versions[workflow.versions.length - 1];
    const { firstStep, firstStepId } = latestVersion;

    if (firstStep && firstStepId) {
      setFirstStepId(firstStepId);
      addStepNodeAndEdge(firstStep);
    }

    setNodesState((prevNodes) => {
      handleSurveyAndWardOptions(workflow.wards, prevNodes);
      return prevNodes;
    });
  };

  const [, drop] = useDrop({
    accept: "NODE",
    drop: (item: any, monitor) => {
      const offset = monitor.getClientOffset();
      const { x, y } = offset ?? { x: 0, y: 0 };

      const position = screenToFlowPosition({
        x,
        y,
      });

      const formikRef =
        React.createRef<FormikProps<IWorkflowRule | IWorkflowResponse>>();

      const newNode = {
        id: uuidv4(),
        type: item.type,
        position,
        data: {
          ruleTypes,
          ruleConditionComparators,
          surveyScoreConditionComparators,
          ruleConditionTriggers,
          responseTypes,
          responseTimes,
          actionTypes,
          surveyOptions,
          measurementTypeOptions,
          articleOptions,
          timezoneOptions: timezones,
          hospitalTimezone,
          fibricheckStatuses,
          aliveCorEcgStatuses,
          sleepStages,
          activityMetrics,
          dateEventTypes,
          spirometryStatuses,
          occurrencePeriods,
          hasPatientsWithCarerEmail,
          ref: formikRef,
          isFirstStep: false,
          hasScheduledRule,
        },
      };

      setNodesState((prevNodes: any) => [...prevNodes, newNode]);
    },
  });

  const nodeTypes = useMemo(
    () => ({ ruleNode: RuleNode, responseNode: ResponseNode }),
    []
  );

  const wardsDropdown = useMemo(() => {
    const groupOptions =
      hospitalGroupWards?.map((ward) => {
        return {
          label: ward.wardName,
          value: ward.wardId.toString(),
          key: uuidv4(),
        };
      }) ?? [];

    // Selected wards that aren't in the hospital-group list would otherwise
    // render as raw ward-ID numbers. Add them as options with a name resolved
    // from the full ward list (falling back to the ID only if it can't be
    // found), so every chip shows a label.
    const knownWardIds = new Set(
      (hospitalGroupWards ?? []).map((ward) => ward.wardId)
    );
    const missingOptions =
      formik.values.wards
        ?.filter((wardId) => !knownWardIds.has(wardId))
        .map((wardId) => {
          const resolved = allWards?.find((ward) => ward.wardId === wardId);

          return {
            label: resolved?.wardName ?? wardId.toString(),
            value: wardId.toString(),
            key: uuidv4(),
          };
        }) ?? [];

    return (
      <SelectDropdown
        placeholder={wardLabel.plural}
        multiple
        value={formik.values.wards?.map(String)}
        options={[...groupOptions, ...missingOptions]}
        onValueChange={(value) => {
          formik.setFieldValue("wards", (value as string[])?.map(Number));
        }}
        className={`${wardsSelectCss()} nopan nodrag`}
      />
    );
  }, [hospitalGroupWards, allWards, formik.values.wards, wardLabel.plural]);

  const handleFlowSectionClick = async (section: string) => {
    if (section === "flow") {
      setSelectedSection("flow");

      mixpanelActions.track(
        `UserAction: FlowTab${workflow?.title ? `[${workflow.title}]` : ""}`
      );
      await handleSurveyAndWardOptions(formik.values.wards);
      setTimeout(() => {
        fitView({ duration: 1000 });
      }, 10);
    } else {
      mixpanelActions.track("UserAction: SettingsTab");
      setSelectedSection("settings");
    }
  };

  useEffect(() => {
    if (
      workflowId !== undefined &&
      !workflow &&
      !isFetching &&
      !areOptionsLoading
    ) {
      setIsWorkflowLoading(true);

      workflowsService.getWorkflowById(workflowId).then((workflow) => {
        setWorkflow(workflow.data);

        handleWorkflowNodes(workflow.data).then(() => {
          formik.setValues({
            title: workflow.data.title,
            description: workflow.data.description,
            wards: workflow.data.wards,
            published: workflow.data.published,
          });

          setIsWorkflowLoading(false);
        });
      });
    }
  }, [workflowId, isFetching, isRefetching, areOptionsLoading]);

  useEffect(() => {
    if (!workflowId && !isFetching && !isRefetching && nodes.length === 0) {
      const formikRef =
        React.createRef<FormikProps<IWorkflowRule | IWorkflowResponse>>();

      const newNode = {
        id: firstStepId,
        type: "ruleNode",
        position: { x: 0, y: 0 } as XYPosition,
        data: {
          ruleTypes,
          ruleConditionComparators,
          surveyScoreConditionComparators,
          ruleConditionTriggers,
          responseTypes,
          responseTimes,
          actionTypes,
          surveyOptions,
          measurementTypeOptions,
          articleOptions,
          timezoneOptions: timezones,
          hospitalTimezone,
          fibricheckStatuses,
          aliveCorEcgStatuses,
          sleepStages,
          activityMetrics,
          dateEventTypes,
          spirometryStatuses,
          occurrencePeriods,
          ref: formikRef,
          isFirstStep: true,
          hasScheduledRule: false,
        },
      };

      setNodesState((prevNodes: any) => [...prevNodes, newNode]);
    }
  }, [workflowId, isFetching, isRefetching, areOptionsLoading]);

  useEffect(() => {
    setNodesState((prevNodes: any) =>
      prevNodes.map((node: any) => ({
        ...node,
        data: { ...node.data, hasPatientsWithCarerEmail },
      }))
    );
  }, [hasPatientsWithCarerEmail]);

  useEffect(() => {
    if (!workflow) return;

    if (
      formik.values.title.trim() !== workflow.title ||
      formik.values.description.trim() !== workflow.description ||
      formik.values.wards !== workflow.wards
    ) {
      setHasSettingsChanged(true);
    } else {
      setHasSettingsChanged(false);
    }
  }, [formik.values]);

  const handleActionButtonsClick = async (
    isPublished: boolean,
    isSave?: boolean
  ) => {
    const validateNodes = async () => {
      const validations = await Promise.all(
        nodes?.map(async (node: any) => {
          const formikRef = node.data.ref?.current;
          if (formikRef) {
            formikRef.submitForm();
            const errors = await formikRef.validateForm();

            return Object.keys(errors).length === 0;
          }
          return false;
        })
      );
      return validations.every(Boolean);
    };

    const handleOnModalClose = async (
      notification?: Notification,
      refetchTable?: boolean,
      saveWorkflow?: boolean
    ) => {
      closeModal();

      if (saveWorkflow) {
        await handleOnSaveWorkflow(isPublished);
      }
    };

    const areNodesValid = await validateNodes();
    await formik.submitForm();
    const settingsFormErrors = await formik.validateForm();
    const isSettingsFormValid = Object.keys(settingsFormErrors).length === 0;

    if (!areNodesValid || !isSettingsFormValid) {
      await handleSurveyAndWardOptions(formik.values.wards);

      setLoadingState(false);
      setSelectedSection(isSettingsFormValid ? "flow" : "settings");
      return;
    }

    if (isSave) {
      await handleOnSaveWorkflow(isPublished);
      return;
    }

    openModal(
      <WorkflowStatusChangeModal
        workflowId={workflowId}
        published={isPublished}
        onClose={handleOnModalClose}
      />,
      {
        width: "623px",
        height: "100%",
        showCloseButton: true,
      }
    );
  };

  const sectionButtons: ISectionButton[] = [
    {
      isSelected: selectedSection === "flow",
      onButtonClick: handleFlowSectionClick,
      label: t("workflows_builder_tabs_flow"),
      name: "flow",
    },
    {
      isSelected: selectedSection === "settings",
      onButtonClick: handleFlowSectionClick,
      label: t("workflows_builder_tabs_settings"),
      name: "settings",
    },
  ];

  return (
    <BuilderLayout
      location="Workflows"
      isPurpleButtonLoading={purpleButtonIsLoading}
      isWhiteButtonLoading={whiteButtonIsLoading}
      isLayoutLoading={isLoading}
      handleOnCancel={handleCancel}
      lastEdited={
        workflow && workflow.versions[workflow.versions.length - 1].created
      }
      handleOnSave={handleActionButtonsClick}
      isPublished={workflow?.published ?? false}
      sectionButtons={sectionButtons}
    >
      <ReactFlowContainer
        style={{ display: selectedSection === "flow" ? "flex" : "none" }}
      >
        <NodeLibrary />
        <div ref={drop} style={{ width: "100%", height: "100%" }}>
          <ReactFlow
            ref={containerRef}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            maxZoom={1}
            minZoom={0.3}
            style={{ width: "100%", height: "100%" }}
            deleteKeyCode={["Delete", "Backspace"]}
            onNodesChange={handleNodesChanges}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            connectionLineType={ConnectionLineType.SmoothStep}
            fitView
            fitViewOptions={{ duration: 1000 }}
          >
            <Background />
            <Controls />
          </ReactFlow>
        </div>
      </ReactFlowContainer>

      <SettingsContainer
        style={{ display: selectedSection === "settings" ? "block" : "none" }}
      >
        <SettingsChildContainer>
          <TextFieldRow
            label={t("workflows_builder_titleLabel")}
            name="title"
            placeholder={t("workflows_builder_titlePlaceholder")}
            value={formik.values.title}
            onChange={formik.handleChange}
            shouldShowErrorMessage={shouldShowErrorMessage}
          />
          <TextFieldRow
            label={t("workflows_builder_descriptionLabel")}
            name="description"
            placeholder={t("workflows_builder_descriptionPlaceholder")}
            value={formik.values.description}
            onChange={formik.handleChange}
            shouldShowErrorMessage={shouldShowErrorMessage}
          />
          <Row isError={shouldShowErrorMessage("wards") !== ""}>
            <SettingsLabel>{wardLabel.plural}</SettingsLabel>
            {wardsDropdown}
            <ErrorMessage isSettingsPageError>
              {shouldShowErrorMessage("wards")}
            </ErrorMessage>
          </Row>

          {workflowId ? (
            <Row isSaveSettings>
              <Button
                type="button"
                label={t("workflows_builder_saveSettings")}
                onClick={handleSaveSettings}
                disabled={!hasSettingsChanged}
              />
            </Row>
          ) : null}
        </SettingsChildContainer>
      </SettingsContainer>
    </BuilderLayout>
  );
}
